import { useAuthProfiles } from '@/api/auth.api';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { AuthStatus } from '@/types/auth/AuthStatus';
import { AuthProfile } from '@/types/auth/AuthProfile';
import { getDashboardPath, getProfileLabel } from '@/utils/authProfile';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Label } from '@/components/ui/label';

type ProfileAccountSelectorProps = {
  currentUserId: string;
};

const ProfileAccountSelector: React.FC<ProfileAccountSelectorProps> = ({
  currentUserId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchProfile } = useAuth();
  const { toastAsync } = useToast();
  const { data: profiles, isLoading } = useAuthProfiles(true);
  const [isSwitching, setIsSwitching] = useState(false);

  if (isLoading || !profiles || profiles.length <= 1) {
    return null;
  }

  const handleChange = async (userId: string) => {
    if (userId === currentUserId || isSwitching) return;

    setIsSwitching(true);
    try {
      const result = await toastAsync(switchProfile(userId), {
        error: t('pages.profile.accountProfileSwitchError'),
      });
      if (result.status === AuthStatus.OK && result.userType) {
        navigate(getDashboardPath(result.userType), { replace: true });
        window.location.reload();
      }
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="px-6 pb-4 flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-gray-950">
          {t('pages.profile.accountProfile')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('pages.profile.accountProfileDescription')}
        </p>
      </div>
      <RadioGroup
        value={currentUserId}
        onValueChange={handleChange}
        className="flex flex-col sm:flex-row flex-wrap gap-6"
        disabled={isSwitching}
      >
        {profiles.map((profile) => (
          <ProfileRadioOption key={profile.userId} profile={profile} />
        ))}
      </RadioGroup>
    </div>
  );
};

const ProfileRadioOption = ({ profile }: { profile: AuthProfile }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-start gap-2">
      <RadioGroupItem
        value={profile.userId}
        id={`profile-${profile.userId}`}
        className="mt-0.5"
      />
      <Label
        htmlFor={`profile-${profile.userId}`}
        className="flex flex-col gap-0.5 cursor-pointer font-normal"
      >
        <span className="text-sm font-medium text-gray-900">
          {getProfileLabel(profile, t)}
        </span>
        {profile.organizationName && (
          <span className="text-xs text-gray-500">
            {profile.organizationName}
          </span>
        )}
      </Label>
    </div>
  );
};

export default ProfileAccountSelector;
